import * as React from "react";
import DatePicker, { CalendarContainer } from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";
// @ts-ignore
import { range } from "lodash";
import { format, getMonth, getYear, isSameDay, isToday, parse } from "date-fns";
import { MIN_YEAR_CUSTOM_DATE_PICKER } from "../constants/index"
import CustomButton from "./CustomButton";

const MyContainer = ({ className, children }: any) => {
  return (
    <div
      style={{
        padding: "24px",
        background: "#fff",
        borderRadius: "16px",
        boxShadow: "0px 5.062px 13.016px 0px rgba(0, 0, 0, 0.17)",
      }}
    >
      <p className="alu-text-base-1 text-sm mb-4">日程を選択してください</p>
      <CalendarContainer className={className}>
        <div style={{ position: "relative" }}>{children}</div>
        <></>
      </CalendarContainer>
    </div>
  );
};

const CustomHeader = ({
  date,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  yearsInput,
}: any) => {
  let years = range(getYear(new Date()) + 1, MIN_YEAR_CUSTOM_DATE_PICKER, -1);
  const [open, setOpen] = React.useState(false);

  const handleBlur = (event: any) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setOpen(false);
    }
  };

  React.useEffect(() => {
    if (yearsInput && yearsInput.length > 0 && date && date.getFullYear() > yearsInput[0]) {
      changeYear(yearsInput[0]);
    }
  }, [date, yearsInput, changeYear]);

  return (
    <div className="flex items-center justify-center gap-6 mb-4">
      <button
        className="hover:bg-[#FFF3E5] rounded-full disabled:bg-transparent"
        type="button"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
      >
        <svg
          className={`${
            prevMonthButtonDisabled
              ? "fill-[#BBB]"
              : "hover:fill-[#343A3D] fill-[#808080]"
          }`}
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path d="M15.4102 16.59L10.8302 12L15.4102 7.41L14.0002 6L8.00016 12L14.0002 18L15.4102 16.59Z" />
        </svg>
      </button>
      <div className={`${open ? "outline outline-1 outline-[#FF861A] hover:bg-white" : ""} relative flex items-center gap-1 rounded px-3 py-1 pr-1 group hover:cursor-pointer hover:bg-[#FFF3E5]`} onBlur={handleBlur} tabIndex={-1}>
        <div
          className="text-sm alu-text-base-2 font-normal"
          onClick={() => setOpen((prev) => !prev)}
        >
          {getYear(date)}年 {getMonth(date) + 1}月
        </div>
        {open && years.length > 0 && (
          <ul className="z-[99] min-w-full rounded absolute top-[calc(100%+2px)] right-0 p-1 bg-white alu-options-box-shadow overflow-x-hidden overflow-y-scroll custom-scrollbar max-h-[10rem]">
            {years.map((option: any, idx: any) => (
              <li
                tabIndex={0}
                key={idx}
                className="p-2 rounded flex items-center gap-2 hover:bg-[#FFF3E5] hover:cursor-pointer"
                onClick={() => {
                  changeYear(option);
                  setOpen(false);
                }}
              >
                {option === getYear(date) ? (
                  <span className="shrink-0">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M8.9999 16.2001L4.7999 12.0001L3.3999 13.4001L8.9999 19.0001L20.9999 7.0001L19.5999 5.6001L8.9999 16.2001Z"
                        fill="#FF861A"
                      />
                    </svg>
                  </span>
                ) : (
                  <span className="h-4 w-4 shrink-0"></span>
                )}
                <p className="flex-1 text-sm leading-6 font-normal alu-text-base-2">
                  {option}年
                </p>
              </li>
            ))}
          </ul>
        )}
        <span
          onClick={() => setOpen((prev) => !prev)}
          className="hover:cursor-pointer group-hover:text-[#FF861A] text-[#343A3D] flex items-center justify-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <g clipPath="url(#clip0_8858_2804)">
              <path
                d="M11.06 5.72656L8 8.7799L4.94 5.72656L4 6.66656L8 10.6666L12 6.66656L11.06 5.72656Z"
                fill="currentColor"
              />
            </g>
            <defs>
              <clipPath id="clip0_8858_2804">
                <rect width="16" height="16" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </span>
      </div>
      <button
        className="hover:bg-[#FFF3E5] rounded-full disabled:bg-transparent"
        type="button"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
      >
        <svg
          className={`${
            nextMonthButtonDisabled
              ? "fill-[#BBB]"
              : "hover:fill-[#343A3D] fill-[#808080]"
          }`}
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path d="M8.58984 7.41L13.1698 12L8.58984 16.59L9.99984 18L15.9998 12L9.99984 6L8.58984 7.41Z" />
        </svg>
      </button>
    </div>
  );
};

const nameOfDayInJP: any = {
  Sunday: "日",
  Monday: "月",
  Tuesday: "火",
  Wednesday: "水",
  Thursday: "木",
  Friday: "金",
  Saturday: "土",
};

const CustomDatePicker = (
  { 
    value, onChange, minDate, error, placeholderText="YYYY年M月D日", maxDate=null, disabled=false, customStyles,
    yearsInput=null, tabindex=undefined
  }
  : any) => {
  const [open, setOpen] = React.useState(false);
  const [calendarKey, setCalendarKey] = React.useState(0);

  const styleBasedErrorProp = !error
    ? "border alu-border active:border-[#FF861A] focus:border-[#FF861A]"
    : "border-2 border-[#F33D62] active:border-[#F33D62] focus:border-[#F33D62] hover:border-[#F33D62] hover:bg-[#FFF3E5]";

  const handleSelect = (date) => {
    if (date && value && isSameDay(date, value)) {
      onChange(null);
    } else {
      onChange(date);
    }

    setOpen(false);
    setCalendarKey(k => k + 1);
  };

  const handleClickOutsite = () => {
    setOpen(false);
    setCalendarKey(prev => prev + 1);
  }

  const formatDate = (date: any) => {
    return date ? format(date, "yyyy年M月d日") : "";
  };

  return (
    <DatePicker
      key={calendarKey}
      tabindex={tabindex}
      icon={
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <mask id="mask0_20160_895" style={{maskType: "alpha"}} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
            <rect width="24" height="24" fill="#808080"/>
          </mask>
          <g mask="url(#mask0_20160_895)">
            <path d="M5 22C4.45 22 3.97917 21.8042 3.5875 21.4125C3.19583 21.0208 3 20.55 3 20V6C3 5.45 3.19583 4.97917 3.5875 4.5875C3.97917 4.19583 4.45 4 5 4H6V2H8V4H16V2H18V4H19C19.55 4 20.0208 4.19583 20.4125 4.5875C20.8042 4.97917 21 5.45 21 6V20C21 20.55 20.8042 21.0208 20.4125 21.4125C20.0208 21.8042 19.55 22 19 22H5ZM5 20H19V10H5V20ZM5 8H19V6H5V8ZM12 14C11.7167 14 11.4792 13.9042 11.2875 13.7125C11.0958 13.5208 11 13.2833 11 13C11 12.7167 11.0958 12.4792 11.2875 12.2875C11.4792 12.0958 11.7167 12 12 12C12.2833 12 12.5208 12.0958 12.7125 12.2875C12.9042 12.4792 13 12.7167 13 13C13 13.2833 12.9042 13.5208 12.7125 13.7125C12.5208 13.9042 12.2833 14 12 14ZM8 14C7.71667 14 7.47917 13.9042 7.2875 13.7125C7.09583 13.5208 7 13.2833 7 13C7 12.7167 7.09583 12.4792 7.2875 12.2875C7.47917 12.0958 7.71667 12 8 12C8.28333 12 8.52083 12.0958 8.7125 12.2875C8.90417 12.4792 9 12.7167 9 13C9 13.2833 8.90417 13.5208 8.7125 13.7125C8.52083 13.9042 8.28333 14 8 14ZM16 14C15.7167 14 15.4792 13.9042 15.2875 13.7125C15.0958 13.5208 15 13.2833 15 13C15 12.7167 15.0958 12.4792 15.2875 12.2875C15.4792 12.0958 15.7167 12 16 12C16.2833 12 16.5208 12.0958 16.7125 12.2875C16.9042 12.4792 17 12.7167 17 13C17 13.2833 16.9042 13.5208 16.7125 13.7125C16.5208 13.9042 16.2833 14 16 14ZM12 18C11.7167 18 11.4792 17.9042 11.2875 17.7125C11.0958 17.5208 11 17.2833 11 17C11 16.7167 11.0958 16.4792 11.2875 16.2875C11.4792 16.0958 11.7167 16 12 16C12.2833 16 12.5208 16.0958 12.7125 16.2875C12.9042 16.4792 13 16.7167 13 17C13 17.2833 12.9042 17.5208 12.7125 17.7125C12.5208 17.9042 12.2833 18 12 18ZM8 18C7.71667 18 7.47917 17.9042 7.2875 17.7125C7.09583 17.5208 7 17.2833 7 17C7 16.7167 7.09583 16.4792 7.2875 16.2875C7.47917 16.0958 7.71667 16 8 16C8.28333 16 8.52083 16.0958 8.7125 16.2875C8.90417 16.4792 9 16.7167 9 17C9 17.2833 8.90417 17.5208 8.7125 17.7125C8.52083 17.9042 8.28333 18 8 18ZM16 18C15.7167 18 15.4792 17.9042 15.2875 17.7125C15.0958 17.5208 15 17.2833 15 17C15 16.7167 15.0958 16.4792 15.2875 16.2875C15.4792 16.0958 15.7167 16 16 16C16.2833 16 16.5208 16.0958 16.7125 16.2875C16.9042 16.4792 17 16.7167 17 17C17 17.2833 16.9042 17.5208 16.7125 17.7125C16.5208 17.9042 16.2833 18 16 18Z" fill="#808080"/>
          </g>
        </svg>
      }
      showIcon
      minDate={minDate}
      maxDate={maxDate}
      open={open}
      onCalendarOpen={() => setOpen(true)}
      onCalendarClose={() => setOpen(false)}
      onInputClick={undefined}
      onClickOutside={() => handleClickOutsite()}
      selected={value}
      value={formatDate(value)}
      onSelect={handleSelect}
      onChange={() => {}}
      placeholderText={placeholderText}
      disabled={disabled}
      shouldCloseOnSelect={true}
      dateFormat={"yyyy/MM/dd"}
      wrapperClassName="w-full"
      popperPlacement="bottom-start"
      calendarContainer={({ ...props }) => (
        <MyContainer
          {...props}
        />
      )}
      openToDate={
        yearsInput?.length
          ? new Date(yearsInput[0], new Date().getMonth(), 1)
          : value
      }
      portalId="root"
      formatWeekDay={(nameOfDay) => nameOfDayInJP[nameOfDay]}
      renderCustomHeader={({ ...props }) => <CustomHeader {...props} yearsInput={yearsInput} />}
      className={`w-full h-[2.25rem] text-sm outline-0 px-4 !pl-[2.375rem] !py-[0.375rem] border rounded ${disabled ? "bg-[#F7F7F7] text-[#BBB] hover:cursor-default active:!border-[#E0E0E0]" : "hover:cursor-pointer hover:border-transparent bg-white active:text-[#343A3D] hover:bg-[#FFF3E5] active:!outline-none focus:!outline-none active:!border-[#FF861A] focus:!border-[#FF861A]"} ${
        styleBasedErrorProp ? styleBasedErrorProp : "alu-border"
      } ${customStyles ? customStyles : ""}`}
    />
  );
};

export default CustomDatePicker;